import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../../firebase";
import { notifyTopRight } from "../toast/SetToast";

const PatientAccountHnLink = ({ account, allowed, onLinked, onBusyChange }) => {
  const [open, setOpen] = useState(false);
  const [hn, setHn] = useState(account.hn || "");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [patient, setPatient] = useState({ hn: "", name: "", loading: false, error: "" });
  useEffect(() => { setOpen(false); setHn(account.hn || ""); setConfirmed(false); setError(""); }, [account.uid, account.hn]);
  useEffect(() => {
    let active = true;
    const selectedHn = hn.trim();
    setConfirmed(false); setPatient({ hn: "", name: "", loading: Boolean(open && selectedHn), error: "" });
    if (!open || !selectedHn) return () => { active = false; };
    const timer = setTimeout(async () => {
      try {
        if (selectedHn.length > 180 || /[\\/\u0000-\u001f]/.test(selectedHn) || [".", ".."].includes(selectedHn)) throw new Error("Enter a valid HN.");
        const saved = await getDoc(doc(db, "patients", selectedHn));
        if (!saved.exists()) throw new Error("HN not found. Enter the patient's existing HN.");
        const record = saved.data();
        if (record.hn && String(record.hn).trim() !== selectedHn) throw new Error("This patient record has a different HN.");
        const name = [record.firstnameTh || record.firstnameTH || record.firstnameEn || record.firstname, record.lastnameTh || record.lastnameTH || record.lastnameEn || record.lastname].filter(Boolean).join(" ");
        if (active) setPatient({ hn: selectedHn, name, loading: false, error: "" });
      } catch (err) { if (active) setPatient({ hn: "", name: "", loading: false, error: err.message || "Unable to check HN." }); }
    }, 350);
    return () => { active = false; clearTimeout(timer); };
  }, [open, hn]);
  const submit = async (event) => {
    event.preventDefault();
    if (!allowed || busy || !confirmed || patient.hn !== hn.trim()) return;
    setBusy(true); onBusyChange(true); setError("");
    try {
      const result = await httpsCallable(functions, "linkPatientPortalAccountHn")({ uid: account.uid, hn: hn.trim(), expectedHn: account.hn || "", confirmLink: true });
      if (!result.data || result.data.linked !== true) throw new Error("HN linking was not confirmed. Refresh Accounts before retrying.");
      onLinked(result.data.hn); setOpen(false); notifyTopRight("Patient portal HN linked");
    } catch (err) { setError(err.message || "Unable to link HN."); }
    finally { setBusy(false); onBusyChange(false); }
  };
  if (!allowed || account.source !== "Patient portal") return null;
  return <div className="border rounded p-3 mt-3">
    <Button size="sm" variant="outline-primary" disabled={busy || account.disabled} onClick={() => { setOpen(!open); setError(""); }}>{open ? "Hide HN form" : account.hn ? "Edit linked HN" : "Link existing HN"}</Button>
    {open && <form className="mt-3" onSubmit={submit}>
      <label htmlFor="patient-account-link-hn">Patient HN</label><input id="patient-account-link-hn" className="form-control" required maxLength={180} value={hn} disabled={busy} onChange={(event) => setHn(event.target.value)} />
      <p className={`small mt-2 ${patient.error ? "text-danger" : "text-muted"}`}>{patient.loading ? "Checking patient…" : patient.error || (patient.hn ? `HN ${patient.hn}: ${patient.name || "Existing patient"}` : "Enter an existing HN and review the matching patient.")}</p>
      <div className="form-check mb-3"><input id="patient-account-link-confirm" className="form-check-input" type="checkbox" required checked={confirmed} disabled={busy || !patient.hn} onChange={(event) => setConfirmed(event.target.checked)} /><label className="form-check-label" htmlFor="patient-account-link-confirm">I have verified that this portal account belongs to this patient.</label></div>
      <p className="small text-muted">Only Super Admin can edit this link. Changing HN clears the previous report releases; reports require a separate release for the linked patient.</p>
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      <Button size="sm" type="submit" disabled={busy || !confirmed || patient.loading || patient.hn !== hn.trim()}>{busy ? "Linking…" : "Save HN link"}</Button>
    </form>}
  </div>;
};
export default PatientAccountHnLink;
